import { useRef } from 'react';
import { useRouter } from '@tanstack/react-router';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTriggerButton,
  useConfirmationModal,
  useModals,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import type { Customer } from '@/services/db/customer/entity';
import { deleteCustomers, markCustomersAsVerified, unsubscribeCustomers } from '@/services/db/customer/mutations';
import { cioId } from '@/services/db/customer/utils';
import { getUserFacingErrorMessage } from '@/services/api/errors';
import { useFeatureFlag, AccountFeatureFlag } from '@/services/auth/feature-flags';
import { useAuth } from '@/services/auth';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import {
  buildBulkDeleteMatch,
  buildMergeLinkParams,
  canAddToManualSegment,
  canDelete,
  canInviteTestUsers,
  canSetEmailTrackingConsent,
  canUnsubscribe,
  inviteOutcomeMessage,
  inviteTestUsersLabel,
  isBulkDeletion,
  isMergeDisabled,
  partitionInviteTestUsers,
  selectedPeopleLabel,
} from '../utils/bulk-actions';
import { useTrackErrorEncountered, type TrackErrorEncountered } from '../hooks';
import { AddPeopleToManualSegmentModal } from './add-people-to-manual-segment-modal';
import { PeopleDeleteConfirmModal } from './people-delete-confirm-modal';
import { PeopleOpenTrackingConsentModal } from './people-open-tracking-consent-modal';

type PeopleBulkActionsProps = {
  workspaceId: number;
  selectedPeople: readonly Customer[];
  allSelected: boolean;
  totalCount: number;
  canEdit: boolean;
  email: string;
  filters: string | null;
  noFiltersApplied: boolean;
  onClearSelection: () => void;
  /** Reload the list (the actions call it per their own branch rules). */
  onReload: () => void;
  /** Grey out rows the backend is deleting asynchronously. */
  onRowsPendingDeletion: (ids: readonly string[]) => void;
};

type Snackbars = ReturnType<typeof useSnackbars>;
type Confirm = ReturnType<typeof useConfirmationModal>;

interface UnsubscribeDeps {
  confirm: Confirm;
  snackbars: Snackbars;
  trackError: TrackErrorEncountered;
  workspaceId: number;
  countLabel: string;
  selectedCioIds: string[];
  onReload: () => void;
}

async function runUnsubscribe({
  confirm,
  snackbars,
  trackError,
  workspaceId,
  countLabel,
  selectedCioIds,
  onReload,
}: UnsubscribeDeps) {
  const isConfirmed = await confirm({
    title: `Unsubscribe ${countLabel}?`,
    message: `Unsubscribing ${countLabel} will opt them out of all messages. This may take some time to process.`,
    confirmLabel: `Unsubscribe ${countLabel}`,
    isCritical: true,
  });
  if (!isConfirmed) return;
  try {
    await unsubscribeCustomers({ workspaceId, internal_ids: selectedCioIds, customer_ids: [] });
    onReload();
    snackbars.create({
      variant: 'default',
      id: 'people.unsubscribe',
      message: `${countLabel} will be unsubscribed soon.`,
    });
  } catch (error) {
    const message = getUserFacingErrorMessage(
      error,
      `${countLabel} couldn't be unsubscribed. Try again or contact support.`,
    );
    snackbars.create({ variant: 'critical', id: 'people.unsubscribe.error', message });
    trackError('People Bulk Unsubscribe', message);
  }
}

interface InviteTestUsersDeps {
  confirm: Confirm;
  snackbars: Snackbars;
  workspaceId: number;
  inviteLabel: string;
  confirmMessage: string;
  selectedPeople: readonly Customer[];
  personSingular: string;
  personPlural: string;
  onReload: () => void;
}

async function runInviteTestUsers({
  confirm,
  snackbars,
  workspaceId,
  inviteLabel,
  confirmMessage,
  selectedPeople,
  personSingular,
  personPlural,
  onReload,
}: InviteTestUsersDeps) {
  const isConfirmed = await confirm({
    title: `${inviteLabel}?`,
    message: confirmMessage,
    confirmLabel: 'Send confirmation emails',
  });
  if (!isConfirmed) return;

  const partition = partitionInviteTestUsers(selectedPeople);
  let invited = 0;
  let requestFailures = 0;
  if (partition.cioIds.length > 0) {
    const result = await markCustomersAsVerified({ workspaceId, customerIds: partition.cioIds });
    invited = result.succeeded;
    requestFailures = result.failed;
  }
  const failed = partition.preFailed + requestFailures;

  if (invited > 0) {
    onReload();
  }

  snackbars.create({
    variant: invited === 0 && failed > 0 ? 'critical' : 'default',
    id: 'people.mark_as_verified_recipients',
    message: inviteOutcomeMessage({
      invited,
      alreadyVerified: partition.alreadyVerified,
      skippedNoEmail: partition.skippedNoEmail,
      failed,
      personSingular,
      personPlural,
    }),
  });
}

/**
 * Synchronous re-entry protection for the non-modal actions (the modals guard
 * themselves): two events landing before a re-render both read stale state,
 * so the latch is a ref.
 */
function useExclusiveAction() {
  const isRunningRef = useRef(false);
  return (action: () => Promise<void>) => {
    if (isRunningRef.current) return;
    isRunningRef.current = true;
    void action().finally(() => {
      isRunningRef.current = false;
    });
  };
}

/**
 * The merge wizard's href, carrying the current search plus the selected pair.
 * While the wizard is still the embedded app's (rollout flag off) the link keeps
 * the `journeys` URL shape it serves; once migrated it points at the Hydra page.
 */
function useMergeHref({
  workspaceId,
  selectedPeople,
  email,
  filters,
}: Pick<PeopleBulkActionsProps, 'workspaceId' | 'selectedPeople' | 'email' | 'filters'>): string {
  const router = useRouter();
  const isMergeMigrated = useFeatureFlag(AccountFeatureFlag.ProfilesMigration);
  const search = { ...buildMergeLinkParams({ selectedPeople, email, filters }) };
  const params = { workspaceId: String(workspaceId) };
  return isMergeMigrated
    ? router.buildLocation({ to: '/workspaces/$workspaceId/profiles/merge', params, search }).href
    : router.buildLocation({ to: '/workspaces/$workspaceId/journeys/people/merge', params, search }).href;
}

interface DeleteActionDeps {
  props: PeopleBulkActionsProps;
  selection: { allSelected: boolean; totalCount: number; selectedCount: number };
  selectedCioIds: string[];
}

/** Opens the type-the-count confirm modal; the modal invokes the delete. */
function useDeleteAction({ props, selection, selectedCioIds }: DeleteActionDeps) {
  const modals = useModals();
  const { workspaceId, selectedPeople, totalCount, noFiltersApplied, filters, email } = props;
  const bulkDeletion = isBulkDeletion(selection);

  const performDelete = async ({ suppress }: { suppress: boolean }) => {
    const match = buildBulkDeleteMatch({ selection, noFiltersApplied, selectedCioIds, filters, email });
    if (match == null) {
      // `canDelete` should never admit a selection state without a valid
      // match; reject so the modal reports the failure instead of confirming
      // a delete that was never sent.
      throw new Error('No valid bulk-delete match for the current selection');
    }
    await deleteCustomers({ workspaceId, ...match, suppress });
    if (bulkDeletion) {
      // Filter matching happens at deletion time, so the exact affected rows
      // are unknown — reload for the updated list.
      props.onReload();
    } else {
      // The backend deletes asynchronously: grey out the deleted rows so the
      // user knows they're pending; they disappear on the next page load.
      props.onRowsPendingDeletion(selectedPeople.map((person) => person.id));
      props.onClearSelection();
    }
  };

  return () => {
    modals.open(
      PeopleDeleteConfirmModal,
      {
        isBulkDeletion: bulkDeletion,
        deletedPeopleCount: bulkDeletion ? totalCount : selectedPeople.length,
        onDelete: performDelete,
      },
      { shouldCloseOnUnmount: true },
    );
  };
}

interface SelectionModalDeps {
  props: PeopleBulkActionsProps;
  selectedCioIds: string[];
  countLabel: string;
  runExclusive: (action: () => Promise<void>) => void;
}

/**
 * The add-to-segment and tracking-consent modals both act on the current
 * selection: on confirm, the selection clears and the list reloads.
 */
function useSelectionModalActions({ props, selectedCioIds, countLabel, runExclusive }: SelectionModalDeps) {
  const modals = useModals();
  const { workspaceId, selectedPeople, onClearSelection, onReload } = props;

  const runSelectionModal = (open: () => ReturnType<typeof modals.open>) =>
    runExclusive(async () => {
      const result = await modals.waitForClose(open());
      if (result.reason === 'confirm') {
        onClearSelection();
        onReload();
      }
    });

  const handleAddToSegmentClick = () =>
    runSelectionModal(() =>
      modals.open(
        AddPeopleToManualSegmentModal,
        { workspaceId, customerIds: selectedCioIds, peopleCountLabel: countLabel },
        { shouldCloseOnUnmount: true },
      ),
    );

  const handleSetConsentClick = () =>
    runSelectionModal(() =>
      modals.open(
        PeopleOpenTrackingConsentModal,
        {
          workspaceId,
          profiles: selectedPeople.map(({ id, attributes }) => ({ id, attributes })),
          peopleCountLabel: countLabel,
        },
        { shouldCloseOnUnmount: true },
      ),
    );

  return { handleAddToSegmentClick, handleSetConsentClick };
}

type MoreActionsMenuProps = {
  mergeHref: string;
  mergeDisabled: boolean;
  mergeTooltip: string | undefined;
  canEdit: boolean;
  showUnsubscribe: boolean;
  onUnsubscribe: () => void;
  showInvite: boolean;
  inviteLabel: string;
  onInvite: () => void;
  showConsent: boolean;
  onConsent: () => void;
  showDelete: boolean;
  deleteDisabled: boolean;
  onDelete: () => void;
};

function MoreActionsMenu({
  mergeHref,
  mergeDisabled,
  mergeTooltip,
  canEdit,
  showUnsubscribe,
  onUnsubscribe,
  showInvite,
  inviteLabel,
  onInvite,
  showConsent,
  onConsent,
  showDelete,
  deleteDisabled,
  onDelete,
}: MoreActionsMenuProps) {
  return (
    <DropdownMenu placement="bottom-start">
      <DropdownMenuTriggerButton
        data-test-people-list-more-actions
        variant="secondary"
        size="sm"
        icon="chevron-down"
        iconPosition="trailing"
      >
        More actions
      </DropdownMenuTriggerButton>
      <DropdownMenuContent>
        <DropdownMenuItem
          data-test-people-list-merge-link
          icon="merge"
          isDisabled={mergeDisabled}
          href={mergeHref}
          tooltip={mergeTooltip}
        >
          Merge
        </DropdownMenuItem>

        {showUnsubscribe ? (
          <DropdownMenuItem
            data-test-people-list-unsubscribe-button
            icon="unsubscribe"
            isDisabled={!canEdit}
            onClick={onUnsubscribe}
          >
            Unsubscribe
          </DropdownMenuItem>
        ) : null}

        {showInvite ? (
          <DropdownMenuItem data-test-people-mark-as-verified-recipients icon="person" onClick={onInvite}>
            {inviteLabel}
          </DropdownMenuItem>
        ) : null}

        {showConsent ? (
          <DropdownMenuItem data-test-people-set-open-tracking-consent icon="view-on" onClick={onConsent}>
            Set email tracking consent
          </DropdownMenuItem>
        ) : null}

        {showDelete ? (
          <DropdownMenuItem
            data-test-people-delete-confirm-modal-button
            icon="delete-trash"
            isDisabled={deleteDisabled}
            onClick={onDelete}
          >
            Delete forever
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * The selection-driven bulk actions for the People list: the always-present
 * More actions menu (Merge / Unsubscribe / Invite test users / Set email
 * tracking consent / Delete forever) plus the Builder-plan Add to segment
 * button. Renders into the PeopleTable's `actionsSlot`, next to the selection
 * count.
 */
export function PeopleBulkActions(props: PeopleBulkActionsProps) {
  const { workspaceId, selectedPeople, allSelected, totalCount, canEdit, email, filters, noFiltersApplied, onReload } =
    props;
  const taxonomy = useProductTaxonomy();
  const snackbars = useSnackbars();
  const confirm = useConfirmationModal();
  const { account } = useAuth();
  const { hasPermission } = usePermissions();
  const isBuilder = isBuilderPlan(account);
  const canDeletePermission = hasPermission('journeys/people/delete');
  const consentFlagEnabled = useFeatureFlag(AccountFeatureFlag.OpenTrackingConsent);

  const personSingular = taxonomy.term('people.singular');
  const personPlural = taxonomy.term('people.plural');

  const selection = { allSelected, totalCount, selectedCount: selectedPeople.length };
  const countLabel = selectedPeopleLabel(selection, { personSingular, personPlural });
  const selectedCioIds = selectedPeople.map(cioId).filter((id) => id != null);

  const runExclusive = useExclusiveAction();
  const trackError = useTrackErrorEncountered();

  const mergeTooltip = !canEdit
    ? 'Contact your admin to access this feature.'
    : `You can only merge 2 ${personPlural} at a time.`;
  const showMergeTooltip = !canEdit || selectedPeople.length > 2;
  const mergeHref = useMergeHref({ workspaceId, selectedPeople, email, filters });

  const inviteLabel = inviteTestUsersLabel(selectedPeople.length);
  const inviteConfirmMessage = `We'll send a confirmation email to ${countLabel} asking them to opt in as test users. ${taxonomy.term('people.titlePlural')} without an email or ${taxonomy.t('people.relativePronoun')} are already a test user will be skipped.`;

  const handleDeleteClick = useDeleteAction({ props, selection, selectedCioIds });
  const { handleAddToSegmentClick, handleSetConsentClick } = useSelectionModalActions({
    props,
    selectedCioIds,
    countLabel,
    runExclusive,
  });

  const handleUnsubscribeClick = () =>
    runExclusive(() =>
      runUnsubscribe({ confirm, snackbars, trackError, workspaceId, countLabel, selectedCioIds, onReload }),
    );

  const handleInviteClick = () =>
    runExclusive(() =>
      runInviteTestUsers({
        confirm,
        snackbars,
        workspaceId,
        inviteLabel,
        confirmMessage: inviteConfirmMessage,
        selectedPeople,
        personSingular,
        personPlural,
        onReload,
      }),
    );

  return (
    <>
      {canAddToManualSegment(selection, { isBuilder }) ? (
        <Button
          data-test-people-list-add-to-segment-button
          isDisabled={!canEdit}
          variant="primary"
          size="sm"
          onClick={handleAddToSegmentClick}
        >
          Add to segment
        </Button>
      ) : null}

      <MoreActionsMenu
        mergeHref={mergeHref}
        mergeDisabled={isMergeDisabled(selection, canEdit)}
        mergeTooltip={showMergeTooltip ? mergeTooltip : undefined}
        canEdit={canEdit}
        showUnsubscribe={canUnsubscribe(selection)}
        onUnsubscribe={handleUnsubscribeClick}
        showInvite={canInviteTestUsers(selection, { isBuilder, canEdit })}
        inviteLabel={inviteLabel}
        onInvite={handleInviteClick}
        showConsent={canSetEmailTrackingConsent(selection, { consentFlagEnabled, canEdit })}
        onConsent={handleSetConsentClick}
        showDelete={canDelete(selection, { noFiltersApplied })}
        deleteDisabled={!canDeletePermission}
        onDelete={handleDeleteClick}
      />
    </>
  );
}
